import React, { useCallback, useState } from 'react';
import OriginTracer from '@atlassiansox/origin-tracing';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useHasSeenIt } from '@atlassian/jira-onboarding-core/src/services/has-seen-it';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { messages } from './messages';
import type { OriginAnalyticsAttributes } from '@atlassiansox/origin-tracing/types';

export const DES_SHARE_LINK_FLAG_ID = 'des-share-link-flag';

export const useShareDesLinkFlag = () => {
	const { formatMessage } = useIntl();
	const { showFlag, dismissFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isLinkCopied, setIsLinkCopied] = useState(false);
	const [, { setHasSeen }] = useHasSeenIt({ hasSeenItKey: 'desShareLinkFlag', skipFetch: true });

	const copyToClipboard = useCallback(
		(url: string, originAnalyticsAttributes: OriginAnalyticsAttributes) => {
			navigator.clipboard
				.writeText(url)
				.then(() => {
					setIsLinkCopied(true);
					showFlag({
						type: 'success',
						title: formatMessage(messages.copyToClipboardSuccess),
						testId: 'des-share-link.copy-success-flag',
						isAutoDismiss: true,
					});
					fireTrackAnalytics(createAnalyticsEvent({}), 'shareDesCopyLink succcesful', {
						atlOrigin: originAnalyticsAttributes,
					});
				})
				.catch(() => {
					showFlag({
						type: 'error',
						title: formatMessage(messages.copyToClipboardError),
						testId: 'des-share-link.copy-error-flag',
						isAutoDismiss: true,
					});
					fireOperationalAnalytics(createAnalyticsEvent({}), 'shareDesCopyLink failed', {
						atlOrigin: originAnalyticsAttributes,
					});
				})
				.finally(() => {
					dismissFlag(DES_SHARE_LINK_FLAG_ID);
				});
		},
		[showFlag, dismissFlag, formatMessage, createAnalyticsEvent],
	);

	const showShareLinkThroughDesFlag = useCallback(
		async (domain: string) => {
			showFlag({
				id: DES_SHARE_LINK_FLAG_ID,
				type: 'info',
				testId: 'des-share-link.action-success-flag',
				title: formatMessage(messages.bannerTitle),
				icon: <InfoIcon label="Info Icon" size="small" />,
				description: formatMessage(messages.bannerText, { domain }),
				isAutoDismiss: false,
				actions: [
					{
						content: messages.actionText,
						onClick: () => {
							const origin = new OriginTracer({ product: 'jira' });
							const originUrl = origin.addToUrl(window.location.href);
							copyToClipboard(originUrl, origin.toAnalyticsAttributes());
							fireUIAnalytics(createAnalyticsEvent({}), 'shareDesCopyLink clicked');
							setHasSeen('desShareLinkFlag');
						},
					},
				],
				onDismissed: () => {
					fireUIAnalytics(createAnalyticsEvent({}), 'shareDesLinkFlagDismiss clicked', {
						isLinkCopied,
					});
					setHasSeen('desShareLinkFlag');
				},
			});

			fireTrackAnalytics(createAnalyticsEvent({}), 'shareDesLinkFlag viewed');
		},
		[showFlag, formatMessage, createAnalyticsEvent, copyToClipboard, isLinkCopied, setHasSeen],
	);

	return { showShareLinkThroughDesFlag };
};
